import React from "react";
import { useNavigate } from "react-router-dom";
import { Star, MapPin, Briefcase, ArrowRight } from "lucide-react";

export default function ExpertCard({ expert, onSave, isSaved }) {
  const navigate = useNavigate();

  return (
    <div
      className="expert-card bg-white border border-[#E4E4E1] rounded-sm overflow-hidden cursor-pointer group"
      onClick={() => navigate(`/expert/${expert.user_id}`)}
      data-testid={`expert-card-${expert.user_id}`}
    >
      {/* Image */}
      <div className="relative h-48 bg-[#F4F4F1] overflow-hidden">
        <img
          src={expert.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert.name || "Expert")}&background=0F172A&color=fff&size=200`}
          alt={expert.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {expert.is_featured && (
          <span className="absolute top-3 left-3 bg-[#064E3B] text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-sm">
            Featured
          </span>
        )}
        {expert.is_verified && (
          <span className="absolute top-3 right-3 bg-white border border-[#E4E4E1] text-[#064E3B] text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-sm">
            Verified
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-semibold text-[#0F172A] text-base leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {expert.name}
          </h3>
          {expert.rating > 0 && (
            <div className="flex items-center gap-1 shrink-0 ml-2">
              <Star size={13} className="text-amber-500 fill-amber-500" />
              <span className="text-xs font-semibold text-[#0F172A]">{expert.rating}</span>
              <span className="text-xs text-[#A1A1AA]">({expert.total_reviews})</span>
            </div>
          )}
        </div>

        <p className="text-xs text-[#52525B] leading-snug mb-3 line-clamp-2">{expert.headline}</p>

        <div className="flex items-center gap-3 mb-3">
          {expert.location && (
            <span className="flex items-center gap-1 text-xs text-[#A1A1AA]">
              <MapPin size={11} /> {expert.location?.split(",")[0]}
            </span>
          )}
          {expert.years_experience > 0 && (
            <span className="flex items-center gap-1 text-xs text-[#A1A1AA]">
              <Briefcase size={11} /> {expert.years_experience}y exp
            </span>
          )}
        </div>

        {/* Industries */}
        {expert.industries?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {expert.industries.slice(0, 2).map((ind) => (
              <span key={ind} className="text-[10px] bg-[#F4F4F1] text-[#52525B] px-2 py-0.5 rounded-sm font-medium">
                {ind}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-[#E4E4E1]">
          <div>
            {expert.min_price > 0 && (
              <span className="text-sm font-bold text-[#0F172A]">
                ₹{expert.min_price?.toLocaleString("en-IN")}
                <span className="text-xs font-normal text-[#A1A1AA]">/session</span>
              </span>
            )}
          </div>
          <button
            className="flex items-center gap-1 text-xs font-semibold text-[#064E3B] hover:text-[#0F172A] transition-colors"
            data-testid={`view-expert-${expert.user_id}`}
          >
            View Profile <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
